import { defineMessages } from "./define";

/** What more than one screen says: the shell, shared verbs, statuses, errors. */
export const common = defineMessages({
  ja: {
    "app.title": "契約書管理",
    "app.navigation": "管理",
    "app.nav.folders": "フォルダ",
    "app.nav.teams": "チーム",
    "app.nav.users": "ユーザー",
    "app.signOut": "サインアウト",
    "app.language": "言語",

    "common.loading": "読み込み中",
    "common.loadMore": "もっと読む",
    "common.add": "追加",
    "common.remove": "外す",
    "common.delete": "削除",
    "common.rename": "名前変更",
    "common.move": "移動",
    "common.cancel": "キャンセル",
    "common.save": "保存",
    "common.close": "閉じる",
    "common.backToList": "契約書一覧へ",

    "admin.allowlist": "管理者（許可リスト）",
    "admin.team": "管理者グループ",

    "ingest.UPLOADED": "アップロード済み",
    "ingest.EXTRACTING": "抽出中",
    "ingest.EXTRACTED": "抽出済み",
    "ingest.APPROVED": "承認済み",
    "ingest.FAILED": "抽出失敗",

    "failure.PAGE_LIMIT_EXCEEDED":
      "ページ数が上限を超えています。分割してアップロードしてください。",
    "failure.PDF_PARSE_FAILED":
      "PDF として読み取れませんでした。破損しているか、パスワードが設定されている可能性があります。",
    "failure.GEMINI_REQUEST_FAILED":
      "抽出サービスへの要求が失敗しました。時間をおいて再実行してください。",
    "failure.PAGE_COVERAGE_INSUFFICIENT":
      "一部のページを読み取れませんでした。抽出済みの範囲は保存されています。",
    "failure.SCHEMA_VALIDATION_FAILED":
      "抽出結果が想定した形式になりませんでした。担当者に連絡してください。",
    "failure.INTERNAL_ERROR":
      "内部エラーが発生しました。時間をおいて再実行してください。",

    "resource.contract": "契約書",
    "resource.folder": "フォルダ",
    "resource.team": "チーム",
    "resource.user": "ユーザー",

    "error.ADMIN_ONLY": "この操作は管理者のみ行えます",
    "error.NOT_FOUND": "{resource}が見つかりません",
    "error.FORBIDDEN_VIEW": "この{resource}を閲覧する権限がありません",
    "error.FORBIDDEN_EDIT": "この{resource}を変更する権限がありません",
    "error.FORBIDDEN_UPLOAD":
      "この{resource}にアップロードする権限がありません",
    "error.CONTRACT_NOT_REVIEWABLE":
      "この契約書は確認できる状態ではありません（{status}）",
    "error.SELF_EDIT_LOCKOUT":
      "自分自身の編集権限を失う変更はできません。先に自分か自分のチームに編集権限を付けてください",
    "error.UPLOAD_GRANT_FOLDER_ONLY":
      "アップロード権限はフォルダにのみ設定できます",
    "error.MOVE_INTO_DESCENDANT": "自分自身またはその配下には移動できません",
    "error.HIERARCHY_TOO_DEEP": "階層が深すぎます（上限 {max} 階層）",
    "error.FOLDER_HAS_SUBFOLDERS": "サブフォルダがあるフォルダは削除できません",
    "error.FOLDER_HAS_CONTRACTS": "契約書が入っているフォルダは削除できません",
    "error.TEAM_HAS_SUBTEAMS": "サブチームがあるチームは削除できません",
    "error.TEAM_HAS_MEMBERS": "メンバーがいるチームは削除できません",
    "error.ADMIN_TEAM_UNDELETABLE":
      "管理者チームは削除できません。メンバーを外して空にすることはできます",
    "error.ADMIN_TEAM_NO_SUBTEAMS":
      "管理者チームの下にはチームを作れません。管理者はユーザー画面から追加してください",
    "error.FILE_TOO_LARGE": "ファイルが大きすぎます（上限 {maxMegabytes} MB）",
    "error.UPLOAD_CANCEL_UPLOADER_ONLY":
      "アップロードを取り消せるのはアップロードした本人だけです",
    "error.UPLOAD_ALREADY_EXTRACTED": "この契約書はすでに抽出が始まっています",
    "error.UPLOAD_ALREADY_SENT": "ファイルはすでにアップロードされています",
    "error.REQUEST_FAILED": "リクエストが失敗しました（{status}）",
    "error.RESPONSE_WITHOUT_DATA": "応答に data が含まれていません",
    "error.UPLOAD_TRANSFER_FAILED":
      "S3 へのアップロードに失敗しました（{status}）",
  },
  en: {
    "app.title": "Contract Manager",
    "app.navigation": "Administration",
    "app.nav.folders": "Folders",
    "app.nav.teams": "Teams",
    "app.nav.users": "Users",
    "app.signOut": "Sign out",
    "app.language": "Language",

    "common.loading": "Loading",
    "common.loadMore": "Load more",
    "common.add": "Add",
    "common.remove": "Remove",
    "common.delete": "Delete",
    "common.rename": "Rename",
    "common.move": "Move",
    "common.cancel": "Cancel",
    "common.save": "Save",
    "common.close": "Close",
    "common.backToList": "Back to contracts",

    "admin.allowlist": "Administrator (allowlist)",
    "admin.team": "Administrators team",

    "ingest.UPLOADED": "Uploaded",
    "ingest.EXTRACTING": "Extracting",
    "ingest.EXTRACTED": "Extracted",
    "ingest.APPROVED": "Approved",
    "ingest.FAILED": "Extraction failed",

    "failure.PAGE_LIMIT_EXCEEDED":
      "The PDF has too many pages. Split it and upload the parts.",
    "failure.PDF_PARSE_FAILED":
      "The file could not be read as a PDF. It may be damaged or password-protected.",
    "failure.GEMINI_REQUEST_FAILED":
      "The extraction service request failed. Try again later.",
    "failure.PAGE_COVERAGE_INSUFFICIENT":
      "Some pages could not be read. What was extracted has been saved.",
    "failure.SCHEMA_VALIDATION_FAILED":
      "The extraction result was not in the expected format. Contact the maintainers.",
    "failure.INTERNAL_ERROR": "An internal error occurred. Try again later.",

    "resource.contract": "contract",
    "resource.folder": "folder",
    "resource.team": "team",
    "resource.user": "user",

    "error.ADMIN_ONLY": "Only an administrator can do this.",
    "error.NOT_FOUND": "The {resource} was not found.",
    "error.FORBIDDEN_VIEW":
      "You do not have permission to view this {resource}.",
    "error.FORBIDDEN_EDIT":
      "You do not have permission to change this {resource}.",
    "error.FORBIDDEN_UPLOAD":
      "You do not have permission to upload to this {resource}.",
    "error.CONTRACT_NOT_REVIEWABLE":
      "This contract is not ready for review ({status}).",
    "error.SELF_EDIT_LOCKOUT":
      "This change would remove your own edit access. Grant edit access to yourself or one of your teams first.",
    "error.UPLOAD_GRANT_FOLDER_ONLY":
      "Upload access can only be granted on a folder.",
    "error.MOVE_INTO_DESCENDANT":
      "It cannot be moved into itself or below itself.",
    "error.HIERARCHY_TOO_DEEP": "Too deeply nested (limit: {max} levels).",
    "error.FOLDER_HAS_SUBFOLDERS":
      "A folder that has subfolders cannot be deleted.",
    "error.FOLDER_HAS_CONTRACTS":
      "A folder that contains contracts cannot be deleted.",
    "error.TEAM_HAS_SUBTEAMS": "A team that has subteams cannot be deleted.",
    "error.TEAM_HAS_MEMBERS": "A team that has members cannot be deleted.",
    "error.ADMIN_TEAM_UNDELETABLE":
      "The administrators team cannot be deleted. You can remove its members instead.",
    "error.ADMIN_TEAM_NO_SUBTEAMS":
      "No team can be created under the administrators team. Add administrators from the Users screen.",
    "error.FILE_TOO_LARGE": "The file is too large (limit: {maxMegabytes} MB).",
    "error.UPLOAD_CANCEL_UPLOADER_ONLY":
      "Only the person who uploaded it can cancel an upload.",
    "error.UPLOAD_ALREADY_EXTRACTED":
      "Extraction has already started for this contract.",
    "error.UPLOAD_ALREADY_SENT": "The file has already been uploaded.",
    "error.REQUEST_FAILED": "The request failed ({status}).",
    "error.RESPONSE_WITHOUT_DATA": "The response contained no data.",
    "error.UPLOAD_TRANSFER_FAILED": "The upload to S3 failed ({status}).",
  },
});
